import {
  Award,
  Banknote,
  FileSearch,
  FileText,
  FolderKey,
  FolderOpen,
  Landmark,
  Receipt,
  ShieldCheck,
  Truck,
  UsersRound,
} from 'lucide-react'

import { TrailerIcon } from '@/components/icons/TrailerIcon'
import type { SidebarLink } from '@/components/sidebar'

/**
 * The products this prototype gives a tab. Its own enum rather than the app's
 * SidebarTab: that one is Insurance, Compliance and Finance, and these options
 * argue for selling the two finance products by name instead.
 */
export enum ProductTab {
  INSURANCE = 'INSURANCE',
  BOOKKEEPER = 'BOOKKEEPER',
  CFO = 'CFO',
  COMPLIANCE = 'COMPLIANCE',
  FUEL = 'FUEL',
}

export const TAB_LABEL: Record<ProductTab, string> = {
  [ProductTab.INSURANCE]: 'Insurance',
  [ProductTab.BOOKKEEPER]: 'Bookkeeper',
  [ProductTab.CFO]: 'CFO',
  [ProductTab.COMPLIANCE]: 'Compliance',
  [ProductTab.FUEL]: 'Fuel',
}

/** Insurance is sold by the agency; everything else is the parent company. */
export const TAB_ENTITY: Record<ProductTab, string> = {
  [ProductTab.INSURANCE]: 'Cai Insurance Services',
  [ProductTab.BOOKKEEPER]: 'Cai Labs, Inc.',
  [ProductTab.CFO]: 'Cai Labs, Inc.',
  [ProductTab.COMPLIANCE]: 'Cai Labs, Inc.',
  [ProductTab.FUEL]: 'Cai Labs, Inc.',
}

/** Section headings, matching ACTIVE_COVERAGE and MANAGE in the sidebar service. */
const ACTIVE_COVERAGE = 'Active coverage'
const MANAGE = 'Manage'

export interface PrototypeNavRow extends SidebarLink {
  group?: string
}

/** Mirrors `_insurance_items` in `backend/core/services/sidebar.py` for an org admin on active coverage. */
const INSURANCE: PrototypeNavRow[] = [
  { title: 'Insurance', url: '/insure/insurance', icon: ShieldCheck, group: ACTIVE_COVERAGE },
  { title: 'Business info', url: '/insure/fleet-details', icon: FolderKey, group: ACTIVE_COVERAGE },
  { title: 'Drivers', url: '/insure/drivers', icon: UsersRound, group: ACTIVE_COVERAGE },
  { title: 'Trucks', url: '/insure/trucks', icon: Truck, group: ACTIVE_COVERAGE },
  { title: 'Trailers', url: '/insure/trailers', icon: TrailerIcon, group: ACTIVE_COVERAGE },
  { title: 'Loss runs', url: '/insure/violations', icon: FileSearch, group: ACTIVE_COVERAGE },
  { title: 'Certificates (COI)', url: '/insure/certificates', icon: Award, group: MANAGE },
  { title: 'Claims', url: '/insure/claims', icon: FolderOpen, group: MANAGE },
  { title: 'Documents', url: '/insure/documents', icon: FileText },
]

/**
 * A stand-in. The books live in the bookkeeper app and are fetched from it, so
 * there is nothing in this repo to mirror.
 */
const BOOKKEEPER: PrototypeNavRow[] = [
  { title: 'Transactions', url: '/finance/transactions', icon: Receipt },
  { title: 'Bank accounts', url: '/finance/accounts', icon: Landmark },
  { title: 'Reports', url: '/finance/reports', icon: Banknote },
]

/** CFO is one page you pin things to, so it has no rows and gets no rail. */
const CFO: PrototypeNavRow[] = []

const ROWS: Record<ProductTab, PrototypeNavRow[]> = {
  [ProductTab.INSURANCE]: INSURANCE,
  [ProductTab.BOOKKEEPER]: BOOKKEEPER,
  [ProductTab.CFO]: CFO,
  /** Nothing behind either of these yet, so neither gets a rail. */
  [ProductTab.COMPLIANCE]: [],
  [ProductTab.FUEL]: [],
}

export const PROTOTYPE_TABS = [ProductTab.INSURANCE, ProductTab.BOOKKEEPER, ProductTab.CFO]

/** A tab's rows, grouped the way the real nav groups them. */
export function navGroups(tab: ProductTab): { label?: string; rows: PrototypeNavRow[] }[] {
  const groups: { label?: string; rows: PrototypeNavRow[] }[] = []
  for (const row of ROWS[tab]) {
    const open = groups.at(-1)
    if (open && open.label === row.group) open.rows.push(row)
    else groups.push({ label: row.group, rows: [row] })
  }
  return groups
}
